import { useCallback, useEffect, useState } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { FaBell, FaComments, FaHeadset, FaRocket, FaShieldAlt } from 'react-icons/fa';
import { usePermissions } from '@/hooks/usePermissions';
import { PermissionName } from '@/types/roles';
import { useFavoriteChannels } from '@/hooks/useFavoriteChannels';
import api from '@/services/api';
import socketService from '@/services/socket';
import { favoritesEventEmitter } from '@/services/favoritesEventEmitter';
import { announcementEventEmitter } from '@/services/announcementEventEmitter';
import { favoritesCache } from '@/services/favoritesCache';
import { announcementsCache } from '@/services/announcementsCache';
import UserSettingsModal from '@/components/settings/UserSettingsModal';
import { useNavigate } from 'react-router-dom';
import { getAvatarUrl } from '@/utils/imageLoader';
import { useToast } from '@/components/common/ToastProvider';

type DashboardResponse = {
  user: {
    id: string;
    username: string;
    email: string | null;
    avatarUrl: string | null;
    status: string;
    lastSeenAt: number | null;
    createdAt: string | null;
    isAdmin: boolean;
    isOwner: boolean;
  };
  preferences: {
    theme: string;
    density: string;
    voiceActivation: string;
    muteOnJoin: boolean;
    notifications: {
      desktopEnabled: boolean;
      pushEnabled: boolean;
      emailEnabled: boolean;
      mentionsOnly: boolean;
      smartMuteDuringDnd: boolean;
    };
  };
  notifications: {
    global: {
      notifyOnMessage: boolean;
      notifyOnMention: boolean;
      notifyOnFriendJoin: boolean;
    } | null;
  };
  favoriteChannels: Array<{
    id: string;
    channelId: string;
    channel: {
      id: string;
      name: string;
      description: string | null;
      isVoice: boolean;
      isPrivate: boolean;
    };
  }>;
};

interface DashboardProps {
  onChannelSelect: (channelId: string) => void;
}

function Dashboard({ onChannelSelect }: DashboardProps) {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [allChannels, setAllChannels] = useState<any[]>([]);
  const [favoriteChannelsList, setFavoriteChannelsList] = useState<any[]>([]);
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [avatarBlobUrl, setAvatarBlobUrl] = useState<string | null>(null);
  const toast = useToast();
  const currentUser = useCurrentUser();

  const navigate = useNavigate();
  const { hasAnyPermission } = usePermissions();
  const canAccessAdmin =
    data?.user.isAdmin ||
    hasAnyPermission([
      PermissionName.EDIT_SERVER_SETTINGS,
      PermissionName.MANAGE_USERS,
      PermissionName.MANAGE_CHANNEL_SETTINGS,
      PermissionName.MANAGE_RETENTION_POLICY,
    ]);
  const { toggleFavorite } = useFavoriteChannels();

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      // Check if we can use cached favorites
      const cachedFavorites = favoritesCache.get();

      // Fetch dismissed announcement IDs from backend and repopulate cache
      const [dashboardRes, channelsRes, announcementsRes, dismissedRes] = await Promise.all([
        api.get('/dashboard'),
        api.get('/channels'),
        api.get('/announcements'),
        api.get('/announcements/dismissed'),
      ]);
      setData(dashboardRes.data);
      setAllChannels(channelsRes.data.channels || []);

      // Use cached favorites if available, otherwise use fresh data from dashboard
      const favorites = dashboardRes.data.favoriteChannels || [];
      if (!cachedFavorites) {
        // Update cache with fresh data
        favoritesCache.set(favorites);
      }
      setFavoriteChannelsList(cachedFavorites || favorites);

      // Repopulate dismissed announcements cache from backend
      const dismissedIds = Array.isArray(dismissedRes.data.dismissed)
        ? dismissedRes.data.dismissed
        : [];
      announcementsCache.setAll(dismissedIds);

      // Filter out dismissed announcements before setting state
      const allAnnouncements = announcementsRes.data.announcements || [];
      setAnnouncements(allAnnouncements.filter((a: any) => !dismissedIds.includes(a.id)));
      setError(null);

      // Remove any dismissed announcements from the cache that are no longer present in the backend
      const validIds = new Set(allAnnouncements.map((a: any) => a.id));
      const filteredCache = dismissedIds.filter((id: string) => validIds.has(id));
      if (filteredCache.length !== dismissedIds.length) {
        announcementsCache.setAll(filteredCache);
      }

      // Load avatar with auth
      if (dashboardRes.data.user.avatarUrl) {
        const avatarUrl = await getAvatarUrl(dashboardRes.data.user.avatarUrl);
        setAvatarBlobUrl(avatarUrl);
      } else {
        setAvatarBlobUrl(null);
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        // Auth error will be handled by App component
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        return;
      }
      setError(err.response?.data?.error || 'Could not load your dashboard right now.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      // Token will be checked by App component's setup check
      return;
    }

    const fontId = 'dashboard-font';
    if (!document.getElementById(fontId)) {
      const link = document.createElement('link');
      link.id = fontId;
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap';
      document.head.appendChild(link);
    }

    fetchDashboard();

    return () => {
      const link = document.getElementById(fontId);
      if (link) {
        document.head.removeChild(link);
      }
    };
  }, [fetchDashboard]);

  const handleFavoritesUpdate = useCallback(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Listen for favorite updates from other components and refresh
  useEffect(() => {
    const handleNewAnnouncement = (newAnnouncement: any) => {
      // Map the incoming WebSocket announcement structure to the expected UI structure
      const formattedAnnouncement = {
        id: newAnnouncement.id,
        type: 'info', // Default type, as backend doesn't provide it
        title: `Announcement from ${newAnnouncement.sender?.username || 'System'}`,
        message: newAnnouncement.message,
        createdAt: newAnnouncement.createdAt,
      };
      setAnnouncements((prevAnnouncements) => [formattedAnnouncement, ...prevAnnouncements]);
    };

    const handleAnnouncementRevoked = (data: { id: string }) => {
      setAnnouncements((prev) => prev.filter((a) => String(a.id) !== String(data.id)));
    };

    const handleUserStatusChange = (statusData: any) => {
      if (
        !statusData?.userId ||
        String(statusData.userId) === String(data?.user?.id) ||
        String(statusData.userId) === String(currentUser?.id)
      ) {
        setData((prev) =>
          prev
            ? {
                ...prev,
                user: {
                  ...prev.user,
                  status: statusData.status || prev.user.status,
                  lastSeenAt:
                    statusData.lastSeenAt ||
                    prev.user.lastSeenAt ||
                    Math.floor(Date.now() / 1000),
                },
              }
            : prev,
        );
      }
    };

    favoritesEventEmitter.on(handleFavoritesUpdate);
    announcementEventEmitter.on(handleNewAnnouncement);
    socketService.on('admin:announcement:revoked', handleAnnouncementRevoked);
    socketService.on('user:preferences-updated', fetchDashboard);
    socketService.on('user:notifications-updated', fetchDashboard);
    socketService.on('user:status-changed', handleUserStatusChange);
    socketService.on('presence:user-online', handleUserStatusChange);
    socketService.on('presence:user-offline', handleUserStatusChange);

    return () => {
      favoritesEventEmitter.off(handleFavoritesUpdate);
      announcementEventEmitter.off(handleNewAnnouncement);
      socketService.off('admin:announcement:revoked', handleAnnouncementRevoked);
      socketService.off('user:preferences-updated', fetchDashboard);
      socketService.off('user:notifications-updated', fetchDashboard);
      socketService.off('user:status-changed', handleUserStatusChange);
      socketService.off('presence:user-online', handleUserStatusChange);
      socketService.off('presence:user-offline', handleUserStatusChange);
    };
  }, [handleFavoritesUpdate, data?.user?.id, currentUser?.id]);

  // Filter out dismissed announcements on fetch
  useEffect(() => {
    setAnnouncements((prev) =>
      prev.filter((a) => !announcementsCache.get().includes(String(a.id))),
    );
  }, []);

  const handleDismissAnnouncement = async (announcementId: string) => {
    try {
      await api.post(`/announcements/${announcementId}/dismiss`);
      announcementsCache.add(announcementId);
      setAnnouncements((prev) => prev.filter((a) => String(a.id) !== String(announcementId)));
      toast.success('Announcement dismissed');
    } catch (error: any) {
      const backendMsg = error?.response?.data?.error;
      if (backendMsg === 'Announcement already dismissed') {
        announcementsCache.add(announcementId);
        setAnnouncements((prev) => prev.filter((a) => String(a.id) !== String(announcementId)));
        toast.info('Announcement was already dismissed');
      } else if (backendMsg) {
        toast.error(backendMsg);
      } else {
        toast.error('Failed to dismiss announcement');
      }
    }
  };

  const userDisplayName = data?.user.username || currentUser?.username || 'Traveler';

  const formatLastSeen = (lastSeenAt: number | null) => {
    if (!lastSeenAt) return 'No activity yet';
    const diffMs = Date.now() - lastSeenAt * 1000;
    if (diffMs < 60 * 1000) return 'Active now';
    if (diffMs < 60 * 60 * 1000) return `${Math.floor(diffMs / (60 * 1000))}m ago`;
    if (diffMs < 24 * 60 * 60 * 1000) return `${Math.floor(diffMs / (60 * 60 * 1000))}h ago`;
    return `${Math.floor(diffMs / (24 * 60 * 60 * 1000))}d ago`;
  };

  const statusBadge = (status: string) => {
    const normalizedStatus = status === 'away' ? 'idle' : status;
    const palette: Record<string, string> = {
      online: 'bg-green-500/20 text-green-300 border border-green-500/30',
      idle: 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30',
      dnd: 'bg-red-500/20 text-red-300 border border-red-500/30',
      offline: 'bg-gray-500/20 text-gray-300 border border-gray-500/30',
    };
    const color = palette[normalizedStatus] || palette.offline;
    const labels: Record<string, string> = {
      online: '🟢 Online',
      idle: '🟡 Idle',
      dnd: '🔴 Do Not Disturb',
      offline: '⚪ Offline',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${color}`}>
        {labels[normalizedStatus] || '⚪ Offline'}
      </span>
    );
  };

  const handleFavoriteChannel = async (channelId: string) => {
    try {
      await toggleFavorite(channelId);
      await fetchDashboard();
    } catch {
      toast.error('Failed to update favorites');
    }
  };

  const handleUnfavoriteChannel = async (channelId: string) => {
    try {
      await toggleFavorite(channelId);
      setFavoriteChannelsList((prev) => prev.filter((f) => f.channelId !== channelId));
    } catch {
      toast.error('Failed to remove favorite');
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-pulse text-brand-on-surface-variant dark:text-brand-on-surface-variant">
          Loading dashboard...
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="card max-w-md w-full space-y-4 text-center">
          <div className="flex justify-center">
            <FaBell className="text-brand-error dark:text-brand-error" size={28} />
          </div>
          <p className="text-lg font-semibold text-brand-on-background dark:text-brand-on-primary">
            {error || 'Dashboard is unavailable right now.'}
          </p>
          <button onClick={fetchDashboard} className="btn btn-primary w-full">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 overflow-auto p-4 md:p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="card rounded-3xl p-6 sm:p-8 border border-brand-outline-variant/60 shadow-xl bg-brand-surface/90 dark:bg-[#1f1c48]/90 backdrop-blur-xl">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-4">
                {avatarBlobUrl ? (
                  <img
                    src={avatarBlobUrl}
                    alt={userDisplayName}
                    className="h-16 w-16 rounded-full object-cover shadow-md"
                  />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-primary-600 dark:bg-primary-500 flex items-center justify-center text-2xl font-bold text-white shadow-md">
                    {userDisplayName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="space-y-1.5 flex-1">
                  <p className="text-xs uppercase tracking-wider font-semibold text-brand-on-surface-variant dark:text-[#c4b5fd]">
                    Welcome back
                  </p>
                  <h2 className="text-2xl md:text-3xl font-bold text-brand-on-background dark:text-[#e0e7ff]">
                    {userDisplayName}
                  </h2>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-brand-on-surface-variant dark:text-[#c4b5fd]">
                    {statusBadge(currentUser?.status || data.user.status)}
                    <span>Last seen {formatLastSeen(data.user.lastSeenAt)}</span>
                    <span className="hidden sm:inline">•</span>
                    <span className="hidden sm:inline">
                      Joined{' '}
                      {data.user.createdAt
                        ? new Date(data.user.createdAt).toLocaleDateString()
                        : '—'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                {canAccessAdmin && (
                  <button
                    onClick={() => navigate('/admin')}
                    className="btn btn-secondary rounded-full flex items-center gap-2"
                  >
                    <FaShieldAlt size={14} />
                    Admin
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 card rounded-3xl p-6 border border-brand-outline-variant/60 shadow-lg bg-brand-surface/90 dark:bg-[#1f1c48]/90">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FaRocket className="text-primary-600 dark:text-primary-400" />
                  <h2 className="text-lg font-bold text-brand-on-background dark:text-[#e0e7ff]">
                    Favorite channels
                  </h2>
                </div>
              </div>
              {favoriteChannelsList.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-brand-outline dark:text-brand-on-surface-variant mb-4">
                    No favorite channels yet.
                  </p>
                  {allChannels.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-brand-outline dark:text-brand-on-surface-variant">
                        Add your favorite channels:
                      </p>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {allChannels.slice(0, 5).map((channel) => (
                          <button
                            key={channel.id}
                            onClick={() => handleFavoriteChannel(channel.id)}
                            className="text-xs px-3.5 py-1.5 rounded-full bg-brand-surface-variant dark:bg-white/10 text-brand-on-surface dark:text-[#e0e7ff] hover:bg-primary-600/30 hover:text-primary-300 transition-all cursor-pointer active:scale-95"
                          >
                            ⭐ {channel.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {favoriteChannelsList
                    .filter((favorite) => favorite.channel) // Filter out deleted channels
                    .map((favorite) => (
                      <div
                        key={favorite.id}
                        onClick={() => onChannelSelect(favorite.channelId)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            onChannelSelect(favorite.channelId);
                          }
                        }}
                        className="rounded-2xl border border-brand-outline-variant/60 bg-brand-surface/60 dark:bg-white/5 p-4 flex flex-col gap-2 hover:bg-white/10 hover:border-primary-500/40 transition-all text-left cursor-pointer active:scale-[0.98] shadow-sm"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <p className="font-semibold text-brand-on-background dark:text-[#e0e7ff] truncate">
                              {favorite.channel.name}
                            </p>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-brand-surface-variant/80 dark:bg-white/10 text-brand-on-surface dark:text-[#c4b5fd] flex-shrink-0">
                              {favorite.channel.isVoice ? <FaHeadset size={10} /> : '#'}
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnfavoriteChannel(favorite.channelId);
                            }}
                            className="text-brand-on-surface-variant hover:text-red-400 transition-colors ml-2 p-1 rounded-full hover:bg-white/10 active:scale-95"
                            title="Remove from favorites"
                            aria-label={`Remove ${favorite.channel.name} from favorites`}
                          >
                            ⭐
                          </button>
                        </div>
                        {favorite.channel.description && (
                          <p className="text-xs text-brand-outline dark:text-[#c4b5fd] line-clamp-2">
                            {favorite.channel.description}
                          </p>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>

            <div className="card rounded-3xl p-6 border border-brand-outline-variant/60 shadow-lg bg-brand-surface/90 dark:bg-[#1f1c48]/90">
              <div className="flex items-center gap-2 mb-4">
                <FaComments className="text-primary-600 dark:text-primary-400" />
                <h2 className="text-lg font-bold text-brand-on-background dark:text-[#e0e7ff]">
                  Announcements
                </h2>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {announcements.length === 0 ? (
                  <p className="text-sm text-brand-outline dark:text-brand-on-surface-variant text-center py-4">
                    No announcements at this time.
                  </p>
                ) : (
                  announcements.map((announcement) => {
                    const bgColor =
                      announcement.type === 'alert'
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                        : announcement.type === 'info'
                          ? 'bg-brand-primary/10 dark:bg-brand-primary/20 border-brand-primary/30 dark:border-brand-primary/30'
                          : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
                    const textColor =
                      announcement.type === 'alert'
                        ? 'text-red-900 dark:text-red-100'
                        : announcement.type === 'info'
                          ? 'text-brand-primary dark:text-brand-on-primary'
                          : 'text-yellow-900 dark:text-yellow-100';
                    const icon =
                      announcement.type === 'alert'
                        ? '⚠️'
                        : announcement.type === 'info'
                          ? 'ℹ️'
                          : '📢';

                    return (
                      <div key={announcement.id} className={`p-3 rounded-lg border ${bgColor}`}>
                        <div className="flex items-start gap-2">
                          <span className="mt-0.5">{icon}</span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${textColor}`}>
                              {announcement.title}
                            </p>
                            <p
                              className={`text-xs ${announcement.type === 'alert' ? 'text-red-700 dark:text-red-300' : announcement.type === 'info' ? 'text-brand-primary dark:text-brand-primary' : 'text-yellow-700 dark:text-yellow-300'} mt-1`}
                            >
                              {announcement.message}
                            </p>
                            <p
                              className={`text-xs ${announcement.type === 'alert' ? 'text-brand-error dark:text-brand-error' : announcement.type === 'info' ? 'text-brand-primary dark:text-brand-primary' : 'text-yellow-500 dark:text-yellow-500'} mt-2`}
                            >
                              {new Date(announcement.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            onClick={() => handleDismissAnnouncement(announcement.id)}
                            className="text-brand-on-surface-variant hover:text-brand-error dark:hover:text-brand-error transition-colors ml-2 focus-visible:ring-2 focus-visible:ring-primary-500 focus:outline-none rounded px-2 py-1"
                            title="Dismiss announcement"
                            aria-label={`Dismiss announcement: ${announcement.title}`}
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {settingsOpen && (
        <UserSettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
      )}
    </>
  );
}

export default Dashboard;
